import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import UsuarioService from '../services/UsuarioService';
import RegionalService from '../services/RegionalService';
import { isAdminUser } from '../utils/adminMenu';
import './SelectAccessContext.css';

const normalizeAccessContext = (context) => {
  if (!context) return null;

  const idRegional = context.idRegional || context.IdRegional || null;
  if (!idRegional) return null;

  return {
    idRegional,
    nombreRegional: context.nombreRegional || context.NombreRegional || context.nombre || context.Nombre || 'Regional',
    nombreRol: context.nombreRol || context.NombreRol || context.roleName || context.RoleName || '',
    activo: context.activo ?? context.Activo ?? true
  };
};

const normalizeAccessContexts = (contexts) =>
  Array.isArray(contexts)
    ? contexts.map(normalizeAccessContext).filter(Boolean)
    : [];

const buildRegionalFromContext = (context) => ({
  id: context.idRegional,
  Id: context.idRegional,
  nombre: context.nombreRegional,
  Nombre: context.nombreRegional,
  activo: context.activo ?? true,
  Activo: context.activo ?? true
});

const getRoleLabel = (roleName) => {
  const normalized = String(roleName || '').toLowerCase();
  if (normalized.includes('adminregional')) return 'Admin regional';
  if (normalized.includes('adminarea')) return 'Admin de area';
  if (normalized.includes('profesor') || normalized.includes('profe')) return 'Profesor';
  if (normalized.includes('admin')) return 'Admin general';
  return roleName || 'Acceso';
};

const getRoleDescription = (roleName, regionalName) => {
  const normalized = String(roleName || '').toLowerCase();
  if (normalized.includes('adminregional')) {
    return `Gestionaras la operacion administrativa del regional ${regionalName}.`;
  }
  if (normalized.includes('adminarea')) {
    return `Ingresaras como admin de area para trabajar dentro de ${regionalName}.`;
  }
  if (normalized.includes('profesor') || normalized.includes('profe')) {
    return `Ingresaras como profesor para tus materias en ${regionalName}.`;
  }
  return `Ingresaras al entorno de ${regionalName}.`;
};

const getRoleIcon = (roleName) => {
  const normalized = String(roleName || '').toLowerCase();
  if (normalized.includes('adminregional')) return 'fas fa-building-columns';
  if (normalized.includes('adminarea')) return 'fas fa-sitemap';
  if (normalized.includes('profesor') || normalized.includes('profe')) return 'fas fa-chalkboard-user';
  if (normalized.includes('admin')) return 'fas fa-shield-halved';
  return 'fas fa-compass';
};

const getDefaultPathForRole = (roleName) =>
  isAdminUser({ roleName }) ? '/admin-dashboard' : '/dashboard';

const onlyActivos = (list) =>
  Array.isArray(list)
    ? list.filter((item) => {
        const activo = item?.activo ?? item?.Activo;
        return activo === undefined ? true : Boolean(activo);
      })
    : [];

const SelectAccessContext = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, applyAccessContext } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingKey, setSubmittingKey] = useState('');
  const [error, setError] = useState('');

  const accessContexts = useMemo(
    () => normalizeAccessContexts(user?.availableAccessContexts),
    [user?.availableAccessContexts]
  );

  const currentAccessContext = useMemo(
    () => normalizeAccessContext(user?.currentAccessContext),
    [user?.currentAccessContext]
  );

  useEffect(() => {
    if (currentAccessContext && !user?.requiresAccessContextSelection) {
      navigate(getDefaultPathForRole(currentAccessContext.nombreRol), { replace: true });
    }
  }, [currentAccessContext, navigate, user?.requiresAccessContextSelection]);

  useEffect(() => {
    if (!accessContexts.length) {
      navigate(getDefaultPathForRole(user?.roleName || user?.role), { replace: true });
    }
  }, [accessContexts.length, navigate, user?.role, user?.roleName]);

  if ((currentAccessContext && !user?.requiresAccessContextSelection) || !accessContexts.length) {
    return null;
  }

  const handleSelectContext = async (context) => {
    const requestKey = `${context.nombreRol}-${context.idRegional}`;
    setIsSubmitting(true);
    setSubmittingKey(requestKey);
    setError('');

    try {
      const response = await UsuarioService.selectRegional(context.idRegional, context.nombreRol);
      const regionales = onlyActivos(response.regionales || response.Regionales || []);
      const areas = onlyActivos(response.areas || response.Areas || []);
      const materias = onlyActivos(response.materias || response.Materias || []);
      const featureFlags = response.ffRegional || response.FFRegional || [];
      const responseContexts = normalizeAccessContexts(response.contextosAcceso || response.ContextosAcceso || accessContexts);
      const selectedContext = normalizeAccessContext(response.contextoAccesoActual || response.ContextoAccesoActual || context) || context;
      const token = response.token || response.Token || user?.token;
      const roleName = response.nombreRol || response.NombreRol || selectedContext.nombreRol;
      const roleId = response.idRol || response.IdRol || user?.roleId;
      const refreshToken = response.refreshToken || response.RefreshToken || '';
      const requirePasswordChange = Boolean(response.requirePasswordChange ?? response.RequirePasswordChange ?? user?.requirePasswordChange ?? false);

      RegionalService.setRegionalData({ regionales, areas, materias });
      RegionalService.setFeatureFlags(Array.isArray(featureFlags) ? featureFlags : []);

      const activeRegional = regionales.find((regional) => {
        const regionalId = regional?.id || regional?.Id;
        return regionalId === selectedContext.idRegional;
      });
      RegionalService.setActiveRegional(activeRegional || buildRegionalFromContext(selectedContext));

      applyAccessContext({
        token,
        roleId,
        roleName,
        role: roleName,
        refreshToken,
        requirePasswordChange,
        availableAccessContexts: responseContexts.length ? responseContexts : accessContexts,
        currentAccessContext: selectedContext,
        requiresAccessContextSelection: false
      });

      const fallbackPath = getDefaultPathForRole(roleName);
      const redirectPath = location.state?.from?.pathname && location.state.from.pathname !== '/select-access-context'
        ? location.state.from.pathname
        : fallbackPath;

      navigate(redirectPath, { replace: true });
    } catch (selectionError) {
      setError(selectionError?.message || 'No pudimos activar el contexto seleccionado.');
    } finally {
      setIsSubmitting(false);
      setSubmittingKey('');
    }
  };

  return (
    <div className="select-regional-page select-access-context-page">
      <div className="select-regional-hero">
        <div className="hero-header">
          <div className="hero-content">
            <p className="eyebrow">Elegir acceso</p>
            <h1>Como queres ingresar</h1>
            <p className="subtitle">
              Tu cuenta tiene permisos distintos segun la regional y el rol. Selecciona el contexto con el que queres trabajar en esta sesion.
            </p>
          </div>
          <button
            type="button"
            className="select-regional-logout"
            onClick={logout}
            title="Cerrar sesion"
          >
            <i className="fas fa-sign-out-alt" />
            <span>Salir</span>
          </button>
        </div>
      </div>

      <div className="select-regional-card">
        <div className="card-header">
          <div>
            <p className="card-kicker">Contextos disponibles</p>
            <h2>Selecciona un perfil de trabajo</h2>
            <p className="card-subtitle">Encontramos {accessContexts.length} opciones posibles para tu cuenta.</p>
          </div>
        </div>

        {error && (
          <div className="select-context-error">
            <i className="fas fa-triangle-exclamation" />
            <span>{error}</span>
          </div>
        )}

        <div className="regional-grid">
          {accessContexts.map((context) => {
            const itemKey = `${context.nombreRol}-${context.idRegional}`;
            const isCurrentSelection = submittingKey === itemKey;
            return (
              <button
                key={itemKey}
                className={`regional-card access-context-card${isCurrentSelection ? ' is-loading' : ''}`}
                type="button"
                onClick={() => handleSelectContext(context)}
                disabled={isSubmitting}
              >
                <div className="regional-icon">
                  <i className={getRoleIcon(context.nombreRol)} />
                </div>
                <div className="regional-info">
                  <div className="regional-meta">
                    <span className="context-role-badge">{getRoleLabel(context.nombreRol)}</span>
                  </div>
                  <h3>{context.nombreRegional}</h3>
                  <p className="regional-desc">{getRoleDescription(context.nombreRol, context.nombreRegional)}</p>
                </div>
                <div className="regional-action">
                  <span>{isCurrentSelection ? 'Ingresando...' : 'Ingresar'}</span>
                  <i className={`fas ${isCurrentSelection ? 'fa-spinner fa-spin' : 'fa-arrow-right'}`} />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SelectAccessContext;
